<template>
  <v-navigation-drawer
      v-model="drawer"
      :rail="rail"
      permanent
      expand-on-hover
      app
      color="surface"
      class="border-r"
      elevation="0">
    <v-list class="py-1">
      <v-list-item
          v-if="user"
          :subtitle="user.email"
          :title="user.name"
          title-class="text-subtitle-2 font-weight-bold"
          prepend-avatar="~/assets/avatar.jpg"
          class="mb-2">
        <template v-slot:append>
          <v-btn
              :icon="!rail ? 'mdi-chevron-left' : 'mdi-chevron-right'"
              variant="text"
              size="small"
              @click.stop="setRail(!rail)">
          </v-btn>
        </template>
      </v-list-item>
    </v-list>

    <v-divider class="mb-2 mx-4"></v-divider>

    <v-list nav density="compact" class="px-2">
      <template v-for="(item, i) in items" :key="i">
        <!-- Item simple -->
        <v-list-item
            v-if="!item.type || item.type === 'item'"
            :to="(item as NavigationItemSimple).to"
            :value="(item as NavigationItemSimple).to ? item : null"
            :active="item.active"
            active-class="bg-primary font-weight-bold"
            rounded="lg"
            class="mb-1">
          <template v-slot:prepend>
            <v-icon :icon="item.icon" class="opacity-80" />
          </template>
          <v-list-item-title class="text-subtitle-2">{{ item.title }}</v-list-item-title>
          <template v-slot:append>
            <v-badge
                v-if="item.badge"
                :content="item.badge"
                color="error"
                inline
                rounded="sm"
                class="ml-2"
            />
          </template>
        </v-list-item>

        <!-- Item action -->
        <v-list-item
            v-else-if="item.type === 'action'"
            active-class="bg-primary font-weight-bold"
            rounded="lg"
            class="mb-1"
            @click="(item as NavigationItemAction).click">
          <template v-slot:prepend>
            <v-icon :icon="item.icon" class="opacity-80" />
          </template>
          <v-list-item-title class="text-subtitle-2">{{ item.title }}</v-list-item-title>
        </v-list-item>

        <!-- Groupe dropdown -->
        <v-list-group v-else-if="item.type === 'group'" :value="(item as NavigationItemGroup).value">
          <template v-slot:activator="{ props }">
            <v-list-item
                v-bind="props"
                active-class="bg-primary font-weight-bold"
                rounded="lg"
                class="mb-1">
              <template v-slot:prepend>
                <v-icon :icon="item.icon" class="opacity-80" />
              </template>
              <v-list-item-title class="text-subtitle-2">{{ item.title }}</v-list-item-title>
            </v-list-item>
          </template>

          <v-list-item
              v-for="(child, j) in (item as NavigationItemGroup).children"
              :key="j"
              :to="(child as NavigationItemSimple).to"
              :active="child.active"
              active-class="bg-primary font-weight-bold"
              rounded="lg"
              class="mb-1">
            <template v-slot:prepend>
              <v-icon :icon="child.icon" class="opacity-80" />
            </template>
            <v-list-item-title class="text-subtitle-2">{{ child.title }}</v-list-item-title>
          </v-list-item>
        </v-list-group>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { useCampaignsStore } from '~/stores/campaigns'


const {drawer} = useApp()
const route = useRoute()
const {user, isAdmin} = useAuth()
const {openHorizonDashboard} = useHorizonAuth()
const campaignsStore = useCampaignsStore()
const rail = ref(false)

function setRail(value: boolean) {
  rail.value = value
  useLocalStorage().setItem('rail', value)
}

onMounted(() => {
  const railStorage = useLocalStorage().getItem('rail')

  if (railStorage !== null) {
    rail.value = Boolean(railStorage)
  }
  
  campaignsStore.fetchErrorCount()
})

const items = computed<NavigationItem[]>(() => [
  {
    title: 'Dashboard',
    to: {name: 'index'},
    icon: 'mdi-view-dashboard-outline'
  },
  {
    title: 'Campagnes',
    to: {name: routeName.campaignIndex},
    icon: 'mdi-message-text-outline',
    badge: campaignsStore.globalStats.error_count || undefined,
    active: route.name?.toString().includes('campaigns')
  },
  {
    title: 'Routeurs',
    to: {name: routeName.routers},
    icon: 'mdi-server-network'
  },
  {
    title: 'Utilisateurs',
    to: {name: routeName.users},
    icon: 'mdi-account-group-outline'
  },
  {
    title: 'Variables',
    to: {name: routeName.variableSchemas},
    icon: 'mdi-variable',
    active: route.name?.toString().includes('variable-schemas')
  },
  ...(isAdmin.value ? [{
    type: 'action' as const,
    title: 'Horizon',
    icon: 'mdi-monitor-dashboard',
    click: openHorizonDashboard
  }] : [])
])
</script>

<style scoped>
.v-navigation-drawer {
  transition: all 0.3s ease-in-out;
}

:deep(.v-list-item--active > .v-list-item__overlay) {
  opacity: 0;
}
</style>
